import styled from "styled-components";
import { GridContainer, SkeletonRow, TableContainer } from "./HomeStyle";
import CardFrame from "./CardFrame";
import SelectChannels from "views/analytics/SelectChannels";
import { useState } from "react";
import { connect } from "react-redux";
import { useMemo } from "react";
import { getAllContentAnalytics, getChannelAnalytics } from "api";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { socialIcons } from "constants/post";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: 24px;
  width: 100%;

  .headerText {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    font-style: normal;
    font-weight: 500;
    line-height: 20px;
  }

  .channelSummaryCard {
    display: flex;
    padding: 16px;
    flex-direction: column;
    gap: 16px;
    border-radius: 8px;
    border: 1px solid #e2e2ea;

    .valueContainer {
      gap: 8px;
      align-items: flex-end;
    }

    .value {
      font-size: 36px;
      font-style: normal;
      font-weight: 500;
    }

    .percentage {
      display: flex;
      padding: 1px 4px;
      justify-content: center;
      align-items: center;
      border-radius: 16px;
      color: #036b26;
      background: #e7f6ec;
    }

    .arrowIcon {
      &.high {
        rotate: 90deg;
      }

      &.low {
        rotate: -90deg;
      }
    }

    @media (max-width: 768px) {
      .header {
        white-space: nowrap;
      }
    }
  }

  .postAnalytics {
    width: 100%;
    overflow: hidden;

    .header {
      padding: 16px 24px;
    }
  }

  .contentContainer {
    width: 100%;

    @media (max-width: 768px) {
      width: 100%;
      overflow-x: auto;
    }
  }
`;

const SkeletonLoader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1em;
  padding: 1em;
  border-radius: 0.5em;
  border: 1px solid #e2e2ea;
  background-color: #f5f5f5;

  .skeletonText {
    width: 40%;
    height: 1em;
    background: #e2e2ea;
    border-radius: 0.25em;
  }

  .valueContainer {
    display: flex;
    gap: 0.5em;
    align-items: flex-end;

    .valueSkeleton {
      width: 100%;
      height: 20px;
      background: #e2e2ea;
      border-radius: 0.25em;
    }

    .percentageSkeleton {
      width: 15%;
      height: 1em;
      background: #e7f6ec;
      border-radius: 1em;
    }
  }

  @media (max-width: 768px) {
    gap: 0.75em;
    padding: 0.75em;

    .skeletonText {
      width: 60%;
      height: 0.875em;
    }

    .valueContainer {
      .valueSkeleton {
        width: 50%;
        height: 2em;
      }

      .percentageSkeleton {
        width: 25%;
        height: 0.875em;
      }
    }
  }

  @media (max-width: 480px) {
    gap: 0.5em;
    padding: 0.5em;

    .skeletonText {
      width: 80%;
      height: 0.75em;
    }

    .valueContainer {
      flex-direction: column;
      align-items: flex-start;

      .valueSkeleton {
        width: 70%;
        height: 1.5em;
      }

      .percentageSkeleton {
        width: 40%;
        height: 0.75em;
      }
    }
  }
`;

const formatNumber = (num) => {
  if (!num && num !== 0) return "-";

  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  }
  return num.toString();
};

const allowedChannels = ["facebook", "instagram", "linkedin"]; // TODO: tiktok is hidden due to missing metrics

const Analytics = ({ brand }) => {
  const history = useHistory();
  const allChannels = useMemo(() => {
    return brand?.socials?.socials.filter((channel) =>
      allowedChannels.includes(channel.platform)
    ) ?? [];
  }, [brand]);

  const [selectedPlatform, setSelectedPlatform] = useState(
    allChannels[0] ?? {}
  );

  const { data: summary, isLoading } = useQuery({
    queryKey: ["channelSummary", selectedPlatform.platform],
    queryFn: () =>
      getChannelAnalytics(selectedPlatform.platform, {
        start_date: moment().subtract(28, "days").format("YYYY-MM-DD"),
        end_date: moment().format("YYYY-MM-DD"),
      }),
    select: (data) => data?.data?.data,
    enabled: !!selectedPlatform.platform,
  });

  const { data: postAnalyticsData, isLoading: loadingPostAnalytics } = useQuery(
    {
      queryKey: ["postAnalytics"],
      queryFn: () =>
        getAllContentAnalytics(
          ["facebook", "instagram", "linkedin", "tiktok"],
          `start_date=${moment()
            .subtract(28, "days")
            .format("YYYY-MM-DD")}&end_date=${moment().format("YYYY-MM-DD")}`
        ),
      select: (data) => data?.data?.data?.data.slice(0, 3),
    }
  );

  return (
    <Wrapper>
      <p className="semiBold">Analytics</p>
      <GridContainer columns="1fr 2fr" gap="24px">
        <CardFrame
          headerText="Channel summary"
          actionBtn="See more"
          onClick={() => history.push("/dashboard/analytics/channel-summary")}
        >
          <div className="fullWidth flexColumn gap24">
            <p className="l3 colorGrey6">Summary for the last one month.</p>
            <SelectChannels
              width="100%"
              height="100%"
              dropdownPosition="left"
              selectedPlatform={selectedPlatform}
              setSelectedPlatform={setSelectedPlatform}
              channels={allChannels}
            />

            <GridContainer columns="1fr 1fr" gap="24px" mbColumn="1fr 1fr">
              {isLoading ? (
                <>
                  {Array.from({ length: 4 }).map((_, index) => (
                    <SkeletonLoader>
                      <div className="skeletonText" />
                      <div className="valueContainer">
                        <div className="valueSkeleton" />
                        <div className="percentageSkeleton" />
                      </div>
                    </SkeletonLoader>
                  ))}
                </>
              ) : (
                <>
                  <div className="channelSummaryCard">
                    <p className="l3 flexRow alignCenter header">Posts</p>
                    <div className="flexRow valueContainer">
                      <p className="value">
                        {formatNumber(summary?.total_posts)}
                      </p>
                    </div>
                  </div>
                  <div className="channelSummaryCard">
                    <p className="l3 flexRow alignCenter header">Impressions</p>
                    <div className="flexRow valueContainer">
                      <p className="value">
                        {formatNumber(summary?.total_impressions)}
                      </p>
                    </div>
                  </div>
                  <div className="channelSummaryCard">
                    <p className="l3 flexRow alignCenter header">Reach</p>
                    <div className="flexRow valueContainer">
                      <p className="value">
                        {formatNumber(summary?.total_reach)}
                      </p>
                    </div>
                  </div>
                  <div className="channelSummaryCard">
                    <p className="l3 flexRow alignCenter header">Engagements</p>
                    <div className="flexRow valueContainer">
                      <p className="value">
                        <p className="value">
                          {formatNumber(summary?.total_engagement)}
                        </p>
                      </p>
                    </div>
                  </div>
                </>
              )}
            </GridContainer>
          </div>
        </CardFrame>
        <CardFrame
          headerText="Post analytics"
          actionBtn="See more"
          padding="0"
          className="postAnalytics"
          onClick={() => history.push("/dashboard/analytics/performance")}
        >
          <>
            <TableContainer>
              <table>
                <thead>
                  <tr>
                    <th>Post</th>
                    <th>Reach</th>
                    <th>Engagement</th>
                    <th>Impressions</th>
                    <th>Likes</th>
                    <th>Comments</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingPostAnalytics
                    ? Array(4)
                        .fill(null)
                        .map((_, index) => (
                          <SkeletonRow key={index}>
                            {Array(6)
                              .fill(null)
                              .map((_, idx) => (
                                <td key={idx}></td>
                              ))}
                          </SkeletonRow>
                        ))
                    : postAnalyticsData?.map((data, index) => (
                        <tr key={index}>
                          <td>
                            <div className="flexRow alignCenter gap8">
                              <div>
                                <img
                                  src={data?.media_url}
                                  alt=""
                                  width="40px"
                                  height="40px"
                                  style={{ borderRadius: "4px" }}
                                />
                              </div>
                              <div>
                                <p className="colorGrey4 headerText">
                                  {data?.content}
                                </p>
                                <p className="textCapitalize flexRow alignCenter gap8">
                                  <img
                                    src={socialIcons.circle[data?.social]}
                                    alt=""
                                    width="16px"
                                    height="16px"
                                  />
                                  {data?.post_type}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td>{data?.reach}</td>
                          <td>{data?.engagement || data?.engagements || 0}</td>
                          <td>{data?.impressions}</td>
                          <td>{data?.likes}</td>
                          <td>{data?.comments}</td>
                        </tr>
                      ))}
                </tbody>
              </table>
            </TableContainer>
          </>
        </CardFrame>
      </GridContainer>
    </Wrapper>
  );
};

const mapStateToProps = (state) => {
  return {
    brand: state.brand,
    user: state.user,
  };
};

export default connect(mapStateToProps)(Analytics);
